import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Sidebar from './Sidebar';
import FloatingImportIndicator from '../common/FloatingImportIndicator';
import ImportDialog from '../common/ImportDialog';
import { useImport } from '../../context/ImportContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const importManager = useImport();
  const [showDialog, setShowDialog] = useState(false);
  const wasMinimizedRef = useRef(false);

  // Only show dialog when explicitly expanded from minimized state
  useEffect(() => {
    const isMinimized = importManager.state.isMinimized;
    const hasProgress = importManager.state.progress !== null;
    
    // If was minimized and now not minimized, show dialog
    if (wasMinimizedRef.current && !isMinimized && hasProgress) {
      setShowDialog(true);
    }
    
    // If minimized or no progress, hide dialog
    if (isMinimized || !hasProgress) {
      setShowDialog(false);
    }
    
    wasMinimizedRef.current = isMinimized;
  }, [importManager.state.isMinimized, importManager.state.progress]);

  const handleDialogClose = () => {
    setShowDialog(false);
    // If import is still running, minimize it
    if (importManager.state.progress && 
        (importManager.state.progress.status === 'processing' || 
         importManager.state.progress.status === 'queued')) {
      importManager.minimize();
    } else {
      importManager.close();
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      overflowX: 'hidden',
      width: '100vw',
      maxWidth: '100vw'
    }}>
      <Sidebar />
      <Box component="main" sx={{ 
        flexGrow: 1, 
        overflowX: 'hidden',
        width: 0, // Important for flex child
        maxWidth: '100%',
        p: 2,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#f8fafc' // Premium background light slate color
      }}>
        <Box sx={{ flexGrow: 1 }}>
          {children}
        </Box>
        <Box component="footer" sx={{ 
          py: 2.5, 
          mt: 4, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            © 2026 EMS. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Link component={RouterLink} to="/sitemap" sx={{ 
              color: 'primary.main', 
              textDecoration: 'none', 
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': { textDecoration: 'underline' } 
            }}>
              Sitemap & Platform Directory
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Floating Import Indicator - shows when minimized */}
      {importManager.state.isMinimized && 
       importManager.state.progress && 
       importManager.state.type && (
        <FloatingImportIndicator
          progress={importManager.state.progress}
          type={importManager.state.type}
          onClick={importManager.expand}
        />
      )}

      {/* Global Import Dialog - shows when expanded */}
      {showDialog && importManager.state.type && (
        <ImportDialog
          open={showDialog}
          onClose={handleDialogClose}
          type={importManager.state.type}
          onImportComplete={() => {
            // Callback handled by ImportContext
          }}
        />
      )}
    </Box>
  );
};

export default Layout;
